/*!
 * 积分商城四组件共用 Swiper 初始化逻辑
 *
 * 统一处理：
 * - DOM ready / window load 兜底初始化
 * - matchMedia 断点变化时的实例销毁与重建
 * - autoplay、分页器进度动画时长（--swiper-interval）同步
 * - 分页器显隐控制与同页多 section 防重复初始化
 */
(function () {
  "use strict";

  // 共享注册表挂在 window 上：同一文件会被四个 section 各加载一次，
  // 跨多次执行共享同一份去重记录，避免同一元素被重复初始化
  if (!(window.PointsSwiperRegistry instanceof Map)) {
    window.PointsSwiperRegistry = new Map();
  }
  const entryMap = window.PointsSwiperRegistry;

  /**
   * 判断当前视口下该 Swiper 是否应该生效
   * 配置了 padMq 时：主断点命中，或 pad 断点命中且 padMode 为 true
   */
  function shouldUseSwiper(entry) {
    if (entry.mq && window.matchMedia(entry.mq).matches) {
      return true;
    }
    if (entry.padMq && window.matchMedia(entry.padMq).matches && entry.padMode) {
      return true;
    }
    return false;
  }

  /** 监听断点变化；兼容旧版 Safari 的 addListener 写法 */
  function bindMq(entry) {
    const queries = [];
    if (entry.mq) {
      queries.push(window.matchMedia(entry.mq));
    }
    if (entry.padMq) {
      queries.push(window.matchMedia(entry.padMq));
    }

    queries.forEach((mq) => {
      const onMqChange = () => syncEntry(entry);
      if (mq.addEventListener) {
        mq.addEventListener("change", onMqChange);
      } else if (mq.addListener) {
        mq.addListener(onMqChange);
      }
    });
  }

  /** 组装 Swiper 实例；分页器、autoplay 按配置按需注入 */
  function initEntry(entry) {
    if (entry.instance) {
      return;
    }
    if (!entry.swiperEl || typeof window.Swiper === "undefined") {
      return;
    }

    const options = Object.assign({}, entry.options, {
      grabCursor: true,
      watchOverflow: true,
      a11y: {
        slideRole: "listitem"
      }
    });

    if (entry.paginationEl) {
      options.pagination = {
        el: entry.paginationEl,
        type: "bullets",
        clickable: true
      };
    }

    if (entry.autoplayDelay > 0) {
      options.autoplay = {
        delay: entry.autoplayDelay,
        pauseOnMouseEnter: true,
        disableOnInteraction: false
      };
    }

    entry.instance = new window.Swiper(entry.swiperEl, options);
    syncInterval(entry);
  }

  function destroyEntry(entry) {
    if (!entry.instance) {
      return;
    }
    entry.instance.destroy(true, true);
    entry.instance = null;
  }

  /** 进度条动画时长与自动播放间隔保持一致 */
  function syncInterval(entry) {
    // 未开启进度时一律写 0ms（即使开启 autoplay），active 分页器即时填满、不播放动画
    const intervalMs = entry.progress
      ? (entry.autoplayDelay || entry.progressFallback)
      : 0;
    entry.sectionRoot.style.setProperty("--swiper-interval", `${intervalMs}ms`);
  }

  /** 分页器显隐：命中 hidePagination.mq 且幻灯片数不超过阈值时隐藏 */
  function updatePaginationVisibility(entry) {
    if (!entry.hidePagination || !entry.paginationEl) {
      return;
    }

    const hideMq = window.matchMedia(entry.hidePagination.mq);
    const slideCount = entry.swiperEl
      ? entry.swiperEl.querySelectorAll(".swiper-slide").length
      : 0;
    const shouldHide = hideMq.matches && slideCount <= entry.hidePagination.maxSlides;
    entry.paginationEl.style.display = shouldHide ? "none" : "";
  }

  function syncEntry(entry) {
    if (shouldUseSwiper(entry)) {
      initEntry(entry);
    } else {
      destroyEntry(entry);
    }
    updatePaginationVisibility(entry);
  }

  function scheduleSync(entry) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => syncEntry(entry));
    } else {
      syncEntry(entry);
    }
    // 图片等资源加载完成后二次校正 Swiper 尺寸
    window.addEventListener("load", () => syncEntry(entry), { once: true });
  }

  function registerEntry(sectionRoot, cfg) {
    if (!cfg || !cfg.el) {
      return;
    }

    const swiperEl = sectionRoot.querySelector(cfg.el);
    if (!swiperEl || entryMap.has(swiperEl)) {
      return;
    }

    const entry = {
      mq: cfg.mq || null,
      padMq: cfg.padMq || null,
      padMode: !!cfg.padMode,
      options: cfg.options || {},
      autoplayDelay: cfg.autoplayDelay || 0,
      progress: !!cfg.progress,
      progressFallback: typeof cfg.progressFallback === "number" ? cfg.progressFallback : 5000,
      hidePagination: cfg.hidePagination || null,
      sectionRoot,
      swiperEl,
      paginationEl: cfg.pagination ? sectionRoot.querySelector(cfg.pagination) : null,
      instance: null
    };

    entryMap.set(swiperEl, entry);
    bindMq(entry);
    scheduleSync(entry);
  }

  function init(config) {
    if (!config || !config.root) {
      return;
    }

    const sectionRoot = document.getElementById(config.root);
    if (!sectionRoot) {
      return;
    }

    (config.swipers || []).forEach((cfg) => {
      registerEntry(sectionRoot, cfg);
    });
  }

  /** 消费 defer 脚本加载前积压的配置 */
  function flushQueue() {
    const queue = window.PointsSwiperQueue;
    if (!queue || !queue.length) {
      return;
    }

    queue.forEach(init);
    window.PointsSwiperQueue = [];
  }

  // 文件会被多次加载，仅首次定义全局 API，避免覆盖其他执行注册的状态
  if (!window.PointsSwiper) {
    window.PointsSwiper = {
      init
    };
  }

  flushQueue();
})();
